import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

/**
 * Skeleton component for PaperDetails page
 * Matches the layout of the actual page for seamless loading transition
 */
const PaperDetailsSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4">
          {/* Back button skeleton */}
          <div className="mb-6">
            <Skeleton className="h-8 w-24" />
          </div>
          
          {/* Glass header container skeleton */}
          <div className="rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 p-4 md:p-5 mb-6">
            {/* Title row with action bar */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-8 md:h-10 w-full max-w-2xl" />
                <Skeleton className="h-6 w-2/3" />
              </div>
              {/* Action bar skeleton */}
              <div className="flex-shrink-0 mt-1">
                <Skeleton className="h-10 w-32 rounded-full" />
              </div>
            </div>
            
            {/* Metadata chips skeleton */}
            <div className="flex flex-wrap gap-2 mb-3">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-32 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-6 w-28 rounded-full" />
            </div>
            
            {/* Authors pills skeleton */}
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-8 w-36 rounded-full" />
              <Skeleton className="h-8 w-32 rounded-full" />
              <Skeleton className="h-8 w-28 rounded-full" />
            </div>
          </div>
          
          {/* Tabs skeleton */}
          <div className="border-b border-border/50 mb-6">
            <div className="flex gap-4">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-28" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>
          
          {/* Content skeleton */}
          <div className="space-y-6">
            {/* Research overview skeleton */}
            <div className="rounded-lg border border-border/50 p-4 space-y-4">
              <Skeleton className="h-6 w-48" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            </div>
            
            {/* Abstract skeleton */}
            <div className="space-y-3">
              <Skeleton className="h-6 w-24" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            </div>
            
            {/* Keywords skeleton */}
            <div className="space-y-3">
              <Skeleton className="h-6 w-24" />
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-6 w-28 rounded-full" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PaperDetailsSkeleton;
